import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Users, Plus, Check, Coins, Ruler } from 'lucide-react';
import {
  coutsApi, LIBELLE_CATEGORIE,
  type CategorieMainOeuvre, type TarifProduit, type Equipe,
} from '@/api/coutsMainOeuvre';
import { mainOeuvreApi } from '@/api/mainOeuvre';
import { toast } from '@/stores/toastStore';
import { formatFCFA } from '@/utils/currency';
import { useAuthStore } from '@/stores/authStore';

/**
 * Les onglets de réglage des coûts de main-d'œuvre — 29/09/2026.
 *
 * Ils vivaient dans un écran séparé. Le PDG a demandé de les intégrer à
 * « Main-d'œuvre » : deux entrées de menu au nom presque identique pour un même
 * sujet, et personne ne sait laquelle ouvrir. Même correction qu'au 21/09 sur
 * les validations.
 *
 * Chaque onglet est autonome — il charge ses propres données — pour se poser
 * tel quel dans l'écran d'accueil sans que celui-ci ait à connaître ce qu'ils
 * affichent.
 *
 * Un principe, visible partout : **un tarif se définit une fois, jamais au
 * chantier.** Un taux retapé chaque matin par un chef est un taux qui dérive,
 * et c'est de la paie.
 */

const CATEGORIES: CategorieMainOeuvre[] = ['armature', 'ferronnerie', 'production'];
const COLONNES = CATEGORIES.length + 3;

const LIBELLE_BAREME: Record<'poteaux' | 'caniveaux', string> = {
  poteaux:   "Poteaux béton armé",
  caniveaux: "Caniveaux",
};

/** Un tarif par produit et par catégorie. Une équipe ne touche que la sienne. */
export function OngletTarifs() {
  // Le métier `generic` marque la holding : c'est déjà le marqueur retenu pour
  // le consolidé financier, autant ne pas en inventer un second.
  const estHolding = useAuthStore((s) => s.filiale?.metier) === 'generic';
  const { data: produits = [], isLoading: chargement } = useQuery({
    queryKey: ['couts-mo', 'tarifs'], queryFn: coutsApi.tarifs, staleTime: 60_000,
  });
  const [edition, setEdition] = useState<{ produit: TarifProduit; categorie: CategorieMainOeuvre } | null>(null);
  const [montant, setMontant] = useState('');
  const [baremeOuvert, setBaremeOuvert] = useState(false);
  const qc = useQueryClient();

  const definir = useMutation({
    mutationFn: (v: { produitId: string; categorie: CategorieMainOeuvre; montantFcfa: number }) =>
      coutsApi.definirTarif(v.produitId, { categorie: v.categorie, montantFcfa: v.montantFcfa }),
    onSuccess: () => {
      toast.success('Tarif enregistré', 'Il s’applique aux journées à venir, pas aux paies déjà faites.');
      qc.invalidateQueries({ queryKey: ['couts-mo'] });
      setEdition(null); setMontant('');
    },
    onError: (e: any) =>
      toast.error('Refusé', e?.response?.data?.error?.message ?? 'Le tarif n’a pas été enregistré.'),
  });

  const retirer = useMutation({
    mutationFn: (id: string) => coutsApi.retirerTarif(id),
    onSuccess: () => {
      toast.success('Tarif retiré', 'Une équipe de cette catégorie ne pourra plus déclarer ce produit.');
      qc.invalidateQueries({ queryKey: ['couts-mo'] });
      setEdition(null); setMontant('');
    },
    onError: (e: any) => toast.error('Refusé', e?.response?.data?.error?.message ?? 'Le tarif n’a pas été retiré.'),
  });

  const incomplets = produits.filter((p) => !p.complet).length;

  // La holding n'a pas de catalogue : ses produits appartiennent aux filiales.
  // Sans cette explication, le PDG connecté à SIM GROUP voit un tableau vide et
  // en conclut que ses tarifs ont disparu — c'est ce qui s'est passé.
  if (estHolding && produits.length === 0 && !chargement) {
    return <RienALaHolding quoi="Les tarifs de main-d'œuvre" />;
  }

  return (
    <>
      {/* La question que tout le monde se pose en découvrant cet onglet : ces
          tarifs étaient ailleurs hier, et ils entrent dans le coût de revient. */}
      <div className="ds-alert-strip ds-alert-i" style={{ marginBottom: 14 }}>
        <span className="ds-alert-ico"><Coins size={20} /></span>
        <div className="ds-alert-txt">
          Ces coûts vivaient dans les matières premières. Ils sont ici depuis le 29/09/2026, parce
          que la main-d'œuvre n'est pas une matière : elle n'a ni stock, ni fournisseur.
          <strong> Le coût de revient de vos produits n'a pas bougé d'un franc</strong> — ces
          montants y entrent toujours, simplement depuis cet onglet.
          {incomplets > 0 && (
            <> {incomplets} produit{incomplets > 1 ? 's' : ''} attend{incomplets > 1 ? 'ent' : ''} encore
            son tarif : une équipe qui le déclare se verra refuser sa paie.</>
          )}
        </div>
      </div>

      <div className="ds-table-panel">
        <div className="ds-table-header">
          <div>
            <div className="ds-table-title">Tarifs par produit</div>
            <div className="ds-table-sub">
              Chaque équipe touche le tarif de sa catégorie : armature, ferronnerie ou production.
              La ferronnerie est facultative. Le total (armature + ferronnerie + production) est ce que la
              main-d'œuvre coûte pour une unité ; il entre dans le coût de revient.
            </div>
          </div>
          {/* Tarifer 40 poteaux un par un, c'est 80 saisies et une faute de frappe
              garantie. Le barème les pose d'un coup — après avoir montré ce qu'il change. */}
          <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => setBaremeOuvert(true)}>
            <Ruler size={14} /> Barèmes
          </button>
        </div>
        <table className="ds-tbl">
          <thead>
            <tr>
              <th style={{ width: 160 }}>Produit</th>
              <th>Désignation</th>
              {CATEGORIES.map((c) => <th key={c} style={{ width: 150 }}>{LIBELLE_CATEGORIE[c]}</th>)}
              <th style={{ width: 130 }}>Total / unité</th>
            </tr>
          </thead>
          <tbody>
            {chargement && <tr><td colSpan={COLONNES} style={{ textAlign: 'center', padding: 32 }}>Chargement…</td></tr>}
            {!chargement && produits.length === 0 && (
              <tr><td colSpan={COLONNES} style={{ textAlign: 'center', padding: 48, fontSize: 13, color: 'var(--tx-3)' }}>
                Aucun produit actif au catalogue.
              </td></tr>
            )}
            {produits.map((p) => (
              <tr key={p.produitId}>
                <td>
                  <span className="ds-ref-code">{p.code}</span>
                  {!p.complet && (
                    <div style={{ marginTop: 3 }}>
                      <span className="ds-bd ds-bd-w" style={{ fontSize: 10 }}>À COMPLÉTER</span>
                    </div>
                  )}
                </td>
                <td style={{ fontSize: 13 }}>{p.designation}</td>
                {CATEGORIES.map((c) => {
                  const t = p.categories[c];
                  return (
                    <td key={c}>
                      <button
                        className={`ds-btn ds-btn-sm ${t || c === 'ferronnerie' ? 'ds-btn-g' : 'ds-btn-p'}`}
                        onClick={() => { setEdition({ produit: p, categorie: c }); setMontant(t ? String(Number(t.montantCents) / 100) : ''); }}
                        style={{ width: '100%', justifyContent: 'center' }}
                      >
                        {t ? formatFCFA(t.montantCents) : c === 'ferronnerie' ? <span style={{ color: 'var(--tx-3)' }}>0 · Définir</span> : <><Plus size={13} /> Définir</>}
                      </button>
                    </td>
                  );
                })}
                <td className="ds-am" style={{ fontWeight: 700 }}>{formatFCFA(p.totalCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {edition && (
        <Dialogue
          titre={`${LIBELLE_CATEGORIE[edition.categorie]} — ${edition.produit.code}`}
          sousTitre={edition.produit.designation}
          onFermer={() => setEdition(null)}
          onValider={() => definir.mutate({
            produitId: edition.produit.produitId, categorie: edition.categorie,
            montantFcfa: Math.round(Number(montant)),
          })}
          validerDesactive={!montant || Number(montant) < 0 || definir.isPending}
          validerLibelle={definir.isPending ? 'Enregistrement…' : 'Enregistrer le tarif'}
        >
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
            Ce que l'entreprise paie pour UNE unité produite (FCFA)
          </label>
          <input
            type="number" min={0} step={100} value={montant} autoFocus
            onChange={(e) => setMontant(e.target.value)}
            placeholder="2500"
            style={champ}
          />
          <div className="ds-alert-strip ds-alert-w" style={{ marginTop: 14 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt" style={{ fontSize: 12 }}>
              Ce tarif <strong>ne réécrit aucune paie déjà faite</strong> : les montants sont figés au
              moment de la déclaration. Il engage les journées à venir.
            </div>
          </div>
          {edition.produit.categories[edition.categorie] && (
            // Retirer (et non mettre 0) : une équipe de cette catégorie qui déclarerait le produit est alors refusée,
            // au lieu d'être payée 0 F en silence (décision PDG du 26/10/2026).
            <button
              className="ds-btn ds-btn-g ds-btn-sm" style={{ marginTop: 12 }} disabled={retirer.isPending}
              onClick={() => retirer.mutate(edition.produit.categories[edition.categorie]!.id)}
            >
              {retirer.isPending ? 'Retrait…' : `Retirer le tarif ${LIBELLE_CATEGORIE[edition.categorie].toLowerCase()} de ce produit`}
            </button>
          )}
        </Dialogue>
      )}

      {baremeOuvert && <DialogueBareme onFermer={() => setBaremeOuvert(false)} />}
    </>
  );
}

/**
 * Les barèmes de main-d'œuvre, appliqués d'un coup après aperçu.
 *
 * Deux barèmes dictés par le PDG, tous deux par équipe :
 *  - poteaux : 2 500 FCFA à 9 m, +500 par mètre ;
 *  - caniveaux : 1 500 FCFA pour un 60 × 60 × 200, +250 par tranche de 10 cm.
 *
 * Trois partis pris, tous payés d'expérience :
 *
 * 1. **On montre avant d'écrire.** Un barème appliqué sans aperçu, c'est un coût
 *    de revient qui bouge sans que personne ne l'ait vu. Le tableau dit, produit
 *    par produit, ce qui part de quoi et arrive à quoi.
 * 2. **Hors de la plage dictée, on demande.** Un poteau de 8 m, un caniveau
 *    40 × 40 : prolonger la droite est cohérent, mais ce n'est pas une consigne
 *    reçue. La case est décochée au départ.
 * 3. **Un produit écarté dit pourquoi.** Sans motif, on le croira oublié et on
 *    le retarifera à la main, en doublon.
 */
function DialogueBareme({ onFermer }: { onFermer: () => void }) {
  const [inclureExtrapoles, setInclureExtrapoles] = useState(false);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['couts-mo', 'baremes'], queryFn: coutsApi.simulerBaremes,
  });

  const appliquer = useMutation({
    mutationFn: () => coutsApi.appliquerBaremes(inclureExtrapoles),
    onSuccess: (r) => {
      toast.success(
        `${r.ecrits} tarif${r.ecrits > 1 ? 's' : ''} écrit${r.ecrits > 1 ? 's' : ''}`,
        r.extrapolesEcartes.length > 0
          ? `${r.extrapolesEcartes.length} produit(s) hors plage laissés de côté — le barème n'y a pas été fixé.`
          : 'Les paies déjà faites ne bougent pas : leurs montants sont figés à la déclaration.',
      );
      qc.invalidateQueries({ queryKey: ['couts-mo'] });
      onFermer();
    },
    onError: (e: any) =>
      toast.error('Refusé', e?.response?.data?.error?.message ?? 'Les barèmes n’ont pas été appliqués.'),
  });

  const lignes     = data?.lignes ?? [];
  const retenues   = lignes.filter((l) => inclureExtrapoles || !l.extrapole);
  const aEcrire    = retenues.flatMap((l) => l.changements).filter((c) => c.effet !== 'inchange');
  const hausses    = aEcrire.filter((c) => c.effet === 'hausse');
  const baisses    = aEcrire.filter((c) => c.effet === 'baisse');
  const extrapoles = lignes.filter((l) => l.extrapole);
  const groupes    = (['caniveaux', 'poteaux'] as const)
    .map((b) => ({ bareme: b, lignes: lignes.filter((l) => l.bareme === b) }))
    .filter((g) => g.lignes.length > 0);

  return (
    <div
      role="dialog" aria-modal="true"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={onFermer}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-0, #fff)', borderRadius: 12, padding: 20, width: '100%',
          maxWidth: 780, maxHeight: '88vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 50px rgba(0,0,0,.25)',
        }}
      >
        <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700 }}>Barèmes de main-d'œuvre</h3>
        <p style={{ margin: '0 0 14px', fontSize: 12.5, color: 'var(--tx-3)' }}>
          {data ? (
            <>
              Poteaux : {data.baremes.poteaux.montantPivotFcfa.toLocaleString('fr-FR')} FCFA à{' '}
              {data.baremes.poteaux.longueurPivot} m, +{data.baremes.poteaux.pasParMetreFcfa} par mètre.
              {' '}Caniveaux : {data.baremes.caniveaux.montantReferenceFcfa.toLocaleString('fr-FR')} FCFA
              {' '}pour un {data.baremes.caniveaux.referenceLargeurCm} × {data.baremes.caniveaux.referenceHauteurCm}
              {' '}× {data.baremes.caniveaux.longueurElementCm}, +{data.baremes.caniveaux.pasFcfa} par
              {' '}{data.baremes.caniveaux.tranchehCm} cm. Toujours par équipe.
            </>
          ) : 'Lecture du catalogue…'}
        </p>

        {isLoading && <div style={{ padding: 32, textAlign: 'center' }}>Chargement…</div>}

        {data && (
          <>
            {(hausses.length > 0 || baisses.length > 0) && (
              <div className="ds-alert-strip ds-alert-w" style={{ marginBottom: 12 }}>
                <span className="ds-alert-ico"><AlertTriangle size={20} /></span>
                <div className="ds-alert-txt" style={{ fontSize: 12.5 }}>
                  <strong>
                    {hausses.length > 0 && `${hausses.length} tarif${hausses.length > 1 ? 's' : ''} augmente${hausses.length > 1 ? 'nt' : ''}`}
                    {hausses.length > 0 && baisses.length > 0 && ', '}
                    {baisses.length > 0 && `${baisses.length} baisse${baisses.length > 1 ? 'nt' : ''}`}.
                  </strong>{' '}
                  Le coût de main-d'œuvre entre dans le coût de revient du produit : il bougera d'autant.
                  Les paies déjà faites, elles, ne bougent pas.
                </div>
              </div>
            )}

            <div style={{ overflow: 'auto', flex: 1, border: '1px solid var(--bd-2, #E5E7EB)', borderRadius: 8 }}>
              <table className="ds-tbl">
                <thead>
                  <tr>
                    <th style={{ width: 150 }}>Produit</th>
                    <th style={{ width: 120 }}>Taille</th>
                    <th style={{ width: 130 }}>Armature</th>
                    <th style={{ width: 130 }}>Production</th>
                    <th>Total / unité</th>
                  </tr>
                </thead>
                {groupes.map((g) => (
                  <tbody key={g.bareme}>
                    <tr>
                      <td colSpan={5} style={{ background: 'var(--bg-1, #F9FAFB)', fontWeight: 700, fontSize: 12 }}>
                        {LIBELLE_BAREME[g.bareme]} — {g.lignes.length}
                      </td>
                    </tr>
                    {g.lignes.map((l) => {
                      const exclu = l.extrapole && !inclureExtrapoles;
                      return (
                        <tr key={l.produitId} style={exclu ? { opacity: 0.4 } : undefined}>
                          <td><span className="ds-ref-code">{l.code}</span></td>
                          <td style={{ fontSize: 12.5 }}>{l.taille}</td>
                          {(['armature', 'production'] as CategorieMainOeuvre[]).map((c) => {
                            const ch = l.changements.find((x) => x.categorie === c);
                            if (!ch) return <td key={c}>—</td>;
                            return (
                              <td key={c} style={{ fontSize: 12.5 }}>
                                {ch.effet === 'inchange' ? (
                                  <span style={{ color: 'var(--tx-3)' }}>{ch.apresFcfa.toLocaleString('fr-FR')}</span>
                                ) : (
                                  <>
                                    <span style={{ color: 'var(--tx-3)', textDecoration: ch.avantFcfa === null ? 'none' : 'line-through' }}>
                                      {ch.avantFcfa === null ? '—' : ch.avantFcfa.toLocaleString('fr-FR')}
                                    </span>
                                    {' → '}
                                    <strong>{ch.apresFcfa.toLocaleString('fr-FR')}</strong>
                                  </>
                                )}
                              </td>
                            );
                          })}
                          <td className="ds-am" style={{ fontWeight: 700 }}>
                            {(l.tarifFcfa * 2).toLocaleString('fr-FR')}
                            {exclu && <span style={{ fontWeight: 400, fontSize: 11 }}> (écarté)</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                ))}
              </table>
            </div>

            {extrapoles.length > 0 && (
              <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', marginTop: 14, cursor: 'pointer' }}>
                <input
                  type="checkbox" checked={inclureExtrapoles}
                  onChange={(e) => setInclureExtrapoles(e.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span style={{ fontSize: 12.5 }}>
                  Inclure les {extrapoles.length} produit(s) hors de la plage fixée
                  {' ('}{extrapoles.map((l) => l.code).join(', ')}{')'}.
                  <span style={{ color: 'var(--tx-3)' }}>
                    {' '}Le barème y descend par prolongement de la droite : c’est cohérent, mais la règle
                    n’a pas été fixée là — c’est une déduction, à confirmer.
                  </span>
                </span>
              </label>
            )}

            {data.ignores.length > 0 && (
              <details style={{ marginTop: 10 }}>
                <summary style={{ fontSize: 12, color: 'var(--tx-3)', cursor: 'pointer' }}>
                  {data.ignores.length} produit(s) qu’aucun barème ne couvre — à tarifer à la main
                </summary>
                <ul style={{ fontSize: 12, color: 'var(--tx-3)', margin: '8px 0 0', paddingLeft: 18 }}>
                  {data.ignores.map((i) => (
                    <li key={i.code}><span className="ds-ref-code">{i.code}</span> — {i.raison}</li>
                  ))}
                </ul>
              </details>
            )}
          </>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
          <button className="ds-btn ds-btn-g" onClick={onFermer}>Annuler</button>
          <button
            className="ds-btn ds-btn-p"
            disabled={!data || aEcrire.length === 0 || appliquer.isPending}
            onClick={() => appliquer.mutate()}
          >
            {appliquer.isPending
              ? 'Application…'
              : aEcrire.length === 0
                ? 'Tout est déjà au barème'
                : `Appliquer — ${aEcrire.length} tarif${aEcrire.length > 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Le taux de la filiale, et celui des ouvriers qui ont le leur. */
export function OngletTaux() {
  const estHolding = useAuthStore((s) => s.filiale?.metier) === 'generic';
  const { data, isLoading: chargement } = useQuery({
    queryKey: ['couts-mo', 'taux'], queryFn: coutsApi.tauxJournaliers, staleTime: 60_000,
  });

  const [edition, setEdition] = useState<{ ouvrierId: string | 'defaut'; nom: string; valeur: string } | null>(null);
  const qc = useQueryClient();

  const enregistrer = useMutation({
    mutationFn: (v: { cible: string; montantFcfa: number | null }) =>
      v.cible === 'defaut'
        ? coutsApi.definirTauxDefaut(v.montantFcfa)
        : coutsApi.definirTauxOuvrier(v.cible, v.montantFcfa),
    onSuccess: () => {
      toast.success('Taux enregistré', 'Il sera appliqué systématiquement.');
      qc.invalidateQueries({ queryKey: ['couts-mo'] });
      setEdition(null);
    },
    onError: (e: any) => toast.error('Refusé', e?.response?.data?.error?.message ?? 'Non enregistré.'),
  });

  // Le garde-fou vient APRÈS tous les hooks : un retour anticipé placé avant
  // eux change leur nombre d'un rendu à l'autre, et React refuse de rendre le
  // composant. C'est le bug qui a fait échouer l'écran le 29/09/2026.
  if (estHolding && !chargement && (data?.ouvriers.length ?? 0) === 0 && !data?.tauxDefautCents) {
    return <RienALaHolding quoi="Les ouvriers et leurs taux" />;
  }

  return (
    <>
      <div className="ds-panel" style={{ marginBottom: 14 }}>
        <div className="ds-panel-h">
          <div className="ds-panel-title">Taux par défaut de la filiale</div>
          <div className="ds-panel-sub">Appliqué à tout ouvrier qui n'a pas le sien</div>
        </div>
        <div className="ds-panel-body" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: 22, fontWeight: 700 }}>
            {data?.tauxDefautCents ? formatFCFA(data.tauxDefautCents) : 'Non défini'}
          </span>
          <button
            className="ds-btn ds-btn-g ds-btn-sm"
            onClick={() => setEdition({
              ouvrierId: 'defaut', nom: 'Taux par défaut',
              valeur: data?.tauxDefautCents ? String(Number(data.tauxDefautCents) / 100) : '',
            })}
          >Modifier</button>
          {!data?.tauxDefautCents && (
            <span style={{ fontSize: 12, color: 'var(--warning)' }}>
              Sans lui, un ouvrier sans taux propre ne peut pas être déclaré à la journée.
            </span>
          )}
        </div>
      </div>

      <div className="ds-table-panel">
        <div className="ds-table-header">
          <div>
            <div className="ds-table-title">Taux par ouvrier</div>
            <div className="ds-table-sub">
              Vider le taux d'un ouvrier le remet sur celui de la filiale
            </div>
          </div>
        </div>
        <table className="ds-tbl">
          <thead>
            <tr>
              <th>Ouvrier</th>
              <th style={{ width: 140 }}>Métier</th>
              <th style={{ width: 140 }}>Taux propre</th>
              <th style={{ width: 150 }}>Appliqué</th>
              <th style={{ width: 110 }}></th>
            </tr>
          </thead>
          <tbody>
            {chargement && <tr><td colSpan={COLONNES} style={{ textAlign: 'center', padding: 32 }}>Chargement…</td></tr>}
            {!chargement && (data?.ouvriers ?? []).length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 48, fontSize: 13, color: 'var(--tx-3)' }}>
                Aucun ouvrier au registre. Les chefs les enregistrent depuis le portail terrain.
              </td></tr>
            )}
            {(data?.ouvriers ?? []).map((o) => (
              <tr key={o.id}>
                <td>
                  <div style={{ fontWeight: 600 }}>{o.nom}</div>
                  <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>{o.telephone}</div>
                </td>
                <td>{o.metier ?? '—'}</td>
                <td className="ds-am">{o.tauxCents ? formatFCFA(o.tauxCents) : <span style={{ color: 'var(--tx-3)' }}>—</span>}</td>
                <td className="ds-am" style={{ fontWeight: 600 }}>
                  {o.tauxApplique ? formatFCFA(o.tauxApplique) : <span style={{ color: 'var(--warning)' }}>aucun</span>}
                </td>
                <td>
                  <button
                    className="ds-btn ds-btn-g ds-btn-sm"
                    onClick={() => setEdition({ ouvrierId: o.id, nom: o.nom, valeur: o.tauxCents ? String(Number(o.tauxCents) / 100) : '' })}
                  >Modifier</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {edition && (
        <Dialogue
          titre={edition.nom}
          sousTitre="Taux journalier, appliqué systématiquement"
          onFermer={() => setEdition(null)}
          onValider={() => enregistrer.mutate({
            cible: edition.ouvrierId,
            montantFcfa: edition.valeur.trim() === '' ? null : Math.round(Number(edition.valeur)),
          })}
          validerDesactive={enregistrer.isPending}
          validerLibelle={enregistrer.isPending ? 'Enregistrement…' : 'Enregistrer'}
        >
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
            Montant en FCFA {edition.ouvrierId !== 'defaut' && '— vide pour utiliser celui de la filiale'}
          </label>
          <input
            type="number" min={0} step={100} value={edition.valeur} autoFocus
            onChange={(e) => setEdition({ ...edition, valeur: e.target.value })}
            placeholder="3500" style={champ}
          />
        </Dialogue>
      )}
    </>
  );
}

/** Les équipes, leur catégorie et leur composition de référence. */
export function OngletEquipes() {
  const estHolding = useAuthStore((s) => s.filiale?.metier) === 'generic';
  const { data: equipes = [], isLoading: chargement } = useQuery({
    queryKey: ['couts-mo', 'equipes'], queryFn: coutsApi.equipes, staleTime: 60_000,
  });

  const [creation, setCreation] = useState(false);
  const [form, setForm] = useState({ code: '', nom: '', categorieMainOeuvre: 'armature' as CategorieMainOeuvre, chefNom: '' });
  const [composition, setComposition] = useState<Equipe | null>(null);
  const qc = useQueryClient();

  const registreQ = useQuery({
    queryKey: ['main-oeuvre', 'ouvriers'], queryFn: mainOeuvreApi.ouvriers,
    staleTime: 60_000, enabled: composition !== null,
  });

  const creer = useMutation({
    mutationFn: () => coutsApi.creerEquipe({
      code: form.code.trim(), nom: form.nom.trim(),
      categorieMainOeuvre: form.categorieMainOeuvre,
      chefNom: form.chefNom.trim() || undefined,
    }),
    onSuccess: () => {
      toast.success('Équipe créée', 'Composez-la, puis elle apparaîtra au chef de chantier.');
      qc.invalidateQueries({ queryKey: ['couts-mo'] });
      setCreation(false); setForm({ code: '', nom: '', categorieMainOeuvre: 'armature', chefNom: '' });
    },
    onError: (e: any) => toast.error('Refusé', e?.response?.data?.error?.message ?? 'Équipe non créée.'),
  });

  const [membres, setMembres] = useState<Set<string>>(new Set());
  const enregistrerMembres = useMutation({
    mutationFn: () => coutsApi.definirMembres(composition!.id, [...membres].map((ouvrierId) => ({ ouvrierId }))),
    onSuccess: () => {
      toast.success('Composition enregistrée', 'Elle pré-remplira la saisie du chef.');
      qc.invalidateQueries({ queryKey: ['couts-mo'] });
      setComposition(null);
    },
    onError: (e: any) => toast.error('Refusé', e?.response?.data?.error?.message ?? 'Non enregistrée.'),
  });

  // Après tous les hooks — voir OngletTaux.
  if (estHolding && !chargement && equipes.length === 0) {
    return <RienALaHolding quoi="Les équipes de production" />;
  }

  return (
    <>
      <div style={{ marginBottom: 14 }}>
        <button className="ds-btn ds-btn-p ds-btn-sm" onClick={() => setCreation(true)}>
          <Plus size={14} /> Nouvelle équipe
        </button>
      </div>

      <div className="ds-table-panel">
        <div className="ds-table-header">
          <div>
            <div className="ds-table-title">Équipes</div>
            <div className="ds-table-sub">
              La composition varie d'un jour à l'autre : celle-ci pré-remplit la saisie du chef,
              elle ne décide jamais de qui est payé
            </div>
          </div>
        </div>
        <table className="ds-tbl">
          <thead>
            <tr>
              <th style={{ width: 110 }}>Code</th>
              <th>Nom</th>
              <th style={{ width: 140 }}>Catégorie</th>
              <th style={{ width: 160 }}>Chef</th>
              <th style={{ width: 120 }}>Membres</th>
              <th style={{ width: 130 }}></th>
            </tr>
          </thead>
          <tbody>
            {chargement && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32 }}>Chargement…</td></tr>}
            {!chargement && equipes.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 48 }}>
                <Users size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>
                  Aucune équipe. Sans équipe, le chef ne peut déclarer qu'à la journée.
                </p>
              </td></tr>
            )}
            {equipes.map((e) => (
              <tr key={e.id}>
                <td><span className="ds-ref-code">{e.code}</span></td>
                <td style={{ fontWeight: 600 }}>{e.nom}</td>
                <td>
                  {e.categorieMainOeuvre
                    ? <span className="ds-bd ds-bd-p" style={{ fontSize: 10 }}>{LIBELLE_CATEGORIE[e.categorieMainOeuvre]}</span>
                    : <span className="ds-bd ds-bd-d" style={{ fontSize: 10 }}>SANS CATÉGORIE</span>}
                </td>
                <td>{e.chefNom ?? '—'}</td>
                <td className="ds-am">{e.membres.length}</td>
                <td>
                  <button
                    className="ds-btn ds-btn-g ds-btn-sm"
                    onClick={() => { setComposition(e); setMembres(new Set(e.membres.map((m) => m.id))); }}
                  >Composer</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creation && (
        <Dialogue
          titre="Nouvelle équipe"
          sousTitre="Sa catégorie décide du tarif qu'elle touche"
          onFermer={() => setCreation(false)}
          onValider={() => creer.mutate()}
          validerDesactive={form.code.trim().length < 2 || form.nom.trim().length < 3 || creer.isPending}
          validerLibelle={creer.isPending ? 'Création…' : 'Créer l’équipe'}
        >
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <label style={etiquette}>Code</label>
              <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="EP-ARM-01" style={champ} />
            </div>
            <div>
              <label style={etiquette}>Nom</label>
              <input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Équipe armature matin" style={champ} />
            </div>
            <div>
              <label style={etiquette}>Catégorie de main-d'œuvre</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {CATEGORIES.map((c) => (
                  <button
                    key={c} onClick={() => setForm({ ...form, categorieMainOeuvre: c })}
                    className={`ds-btn ds-btn-sm ${form.categorieMainOeuvre === c ? 'ds-btn-p' : 'ds-btn-g'}`}
                  >{LIBELLE_CATEGORIE[c]}</button>
                ))}
              </div>
              <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--tx-3)', lineHeight: 1.45 }}>
                Chaque équipe touche le tarif de sa catégorie sur le produit : armature, ferronnerie
                (soudeurs) ou production. Plusieurs équipes se partagent ainsi le même produit.
              </p>
            </div>
            <div>
              <label style={etiquette}>Chef (facultatif)</label>
              <input value={form.chefNom} onChange={(e) => setForm({ ...form, chefNom: e.target.value })} style={champ} />
            </div>
          </div>
        </Dialogue>
      )}

      {composition && (
        <Dialogue
          titre={`Composition — ${composition.nom}`}
          sousTitre="Cochez les habitués ; le chef confirmera les présents chaque jour"
          onFermer={() => setComposition(null)}
          onValider={() => enregistrerMembres.mutate()}
          validerDesactive={enregistrerMembres.isPending}
          validerLibelle={enregistrerMembres.isPending ? 'Enregistrement…' : 'Enregistrer la composition'}
        >
          <div style={{ maxHeight: 340, overflow: 'auto', border: '1px solid var(--bd-1, #E5E7EB)', borderRadius: 8 }}>
            {(registreQ.data ?? []).length === 0 && (
              <p style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--tx-3)' }}>
                Aucun ouvrier au registre. Les chefs les enregistrent depuis le portail terrain.
              </p>
            )}
            {(registreQ.data ?? []).map((o) => {
              const coche = membres.has(o.id);
              return (
                <button
                  key={o.id}
                  onClick={() => {
                    const s = new Set(membres);
                    if (coche) s.delete(o.id); else s.add(o.id);
                    setMembres(s);
                  }}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                    background: coche ? 'var(--bg-1, #F3F4F6)' : 'transparent', border: 'none',
                    borderBottom: '1px solid var(--bd-1, #E5E7EB)', cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <span style={{
                    width: 20, height: 20, borderRadius: 4, flexShrink: 0,
                    border: `1px solid ${coche ? 'var(--primary)' : 'var(--bd-2, #D1D5DB)'}`,
                    background: coche ? 'var(--primary)' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {coche && <Check size={13} color="#fff" />}
                  </span>
                  <span style={{ flexGrow: 1 }}>
                    <span style={{ display: 'block', fontWeight: 600, fontSize: 13 }}>{o.nom}</span>
                    <span style={{ display: 'block', fontSize: 11, color: 'var(--tx-3)' }}>
                      {o.metier ?? 'Métier non précisé'} · {o.telephone}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
          <p style={{ margin: '10px 0 0', fontSize: 11, color: 'var(--tx-3)', lineHeight: 1.45 }}>
            Un membre retiré est désactivé, jamais supprimé : ses journées passées restent
            rattachées à l'équipe pour laquelle il a travaillé.
          </p>
        </Dialogue>
      )}
    </>
  );
}

// ── Petit dialogue commun aux trois onglets ────────────────────────────────
function Dialogue({
  titre, sousTitre, children, onFermer, onValider, validerDesactive, validerLibelle,
}: {
  titre: string; sousTitre?: string; children: React.ReactNode;
  onFermer: () => void; onValider: () => void;
  validerDesactive?: boolean; validerLibelle: string;
}) {
  return (
    <div
      role="dialog" aria-modal="true"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={onFermer}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--bg-0, #fff)', borderRadius: 12, padding: 20, width: '100%', maxWidth: 520, boxShadow: '0 20px 50px rgba(0,0,0,.25)' }}
      >
        <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700 }}>{titre}</h3>
        {sousTitre && <p style={{ margin: '0 0 14px', fontSize: 12.5, color: 'var(--tx-3)' }}>{sousTitre}</p>}
        {children}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
          <button className="ds-btn ds-btn-g" onClick={onFermer}>Annuler</button>
          <button className="ds-btn ds-btn-p" disabled={validerDesactive} onClick={onValider}>{validerLibelle}</button>
        </div>
      </div>
    </div>
  );
}

const champ: React.CSSProperties = {
  width: '100%', padding: '9px 12px', border: '1px solid var(--bd-2, #D1D5DB)',
  borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
};
const etiquette: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6,
};

/**
 * Ce que la holding voit à la place d'un tableau vide.
 *
 * SIM GROUP ne fabrique rien : ses produits, ses ouvriers et ses équipes
 * appartiennent aux filiales. Sans ce message, le PDG connecté à la holding
 * voit trois onglets vides et en conclut que ses données ont disparu — c'est
 * exactement ce qui s'est passé le 29/09/2026.
 */
export function RienALaHolding({ quoi }: { quoi: string }) {
  return (
    <div className="ds-alert-strip ds-alert-w">
      <span className="ds-alert-ico"><AlertTriangle size={20} /></span>
      <div className="ds-alert-txt">
        <strong>SIM GROUP ne fabrique pas.</strong> {quoi} appartiennent aux filiales — aujourd'hui
        Leadbeton CI. Pour les consulter ou les modifier, ouvrez la filiale depuis{' '}
        <a href="/holding" style={{ color: 'var(--primary)', fontWeight: 600 }}>Holding</a> en
        « Vue PDG ». Cet écran affichera alors les siennes.
      </div>
    </div>
  );
}
